import React, { useState, useEffect, useMemo } from "react";
import FitNumber from "../../common/FitNumber";
import { Plus, Minus } from "lucide-react";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../../../components/common/GeneralComponent";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchData } from "../../../utils/functions";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import DashboardSearchFilters from "../../common/DashboardSearchFilters";
import NoData, { hasData } from "../../common/NoData";
import SharedDonut from "../../common/DonutChart";
import DashboardBarChart from "../../common/DashboardBarChart";
import {
  BRAND,
  NEUTRAL,
  STATUS_COLORS,
  getPaletteColor,
} from "../../../utils/chartTheme";
import { formatCount } from "../../../utils/numberFormat";
const OWNERSHIP_TYPES = ["Govt", "Rented", "Community", "Donated"];
const DonutChart = ({ data, centerLabel }) => {
  const valueOf = Object.fromEntries((data || []).map((item) => [item.name, item.value]));
  return (
    <SharedDonut
      items={OWNERSHIP_TYPES.map((name, index) => ({
        name,
        value: valueOf[name] || 0,
        color: getPaletteColor(index),
      }))}
      centerLabel={centerLabel}
    />
  );
};
const SchoolsurveyedCards = ({ surveyed, approved, rejected }) => {
  const pending = surveyed - approved - rejected;
  const ActivityTypeCard = ({ title, count, variant = "default" }) => {
    const borderColors = {
      default: "#ededed",
      approved: BRAND.teal,
      rejected: getPaletteColor(2),
      pending: STATUS_COLORS.planned,
    };
    const borderColor = borderColors[variant] || borderColors.default;
    return (
      <div>
        <div
          className="bg-[#f7f7f7] text-white h-28 p-2.5 rounded-3xl border-2 hover:border-teal-300 transition-all duration-300 hover:shadow-md"
          style={{ borderColor }}
        >
          <h3 className="text-2xl text-center mt-4 mb-0 text-[#3A3A3A] p-0 font-bold text-balance">
            <FitNumber value={count ?? 0} text={formatCount(count ?? 0)} />
          </h3>
          <h6 className="text-xs text-center p-0 m-0 text-[#787878] font-semibold">
            {title}
          </h6>
        </div>
      </div>
    );
  };
  return (
    <div className="grid grid-cols-3 mt-3 gap-2">
      <ActivityTypeCard title="Approved" count={approved} variant="approved" />
      <ActivityTypeCard title="Pending" count={pending} variant="pending" />
      <ActivityTypeCard title="Rejected" count={rejected} variant="rejected" />
    </div>
  );
};
const AllocatedResourcesDonut = ({ libraryBooks, computers }) => (
  <SharedDonut
    items={[
      { name: "Library Books", value: libraryBooks || 0, color: BRAND.teal },
      { name: "Computers", value: computers || 0, color: BRAND.gold },
    ]}
  />
);

const SchoolLevelDistrictChart = ({ data }) => (
  <DashboardBarChart
    categories={(data || []).map((item) => item.district_name)}
    series={[
      { name: "Primary", data: (data || []).map((item) => item.primary) },
      { name: "Middle", data: (data || []).map((item) => item.middle) },
      { name: "Secondary", data: (data || []).map((item) => item.secondary) },
      { name: "Higher Secondary", data: (data || []).map((item) => item.higher_secondary) },
    ]}
    unit="schools"
  />
);

const SCHOOL_TYPES = ["Government", "Private", "NGO Based", "Community-Based"];

const SchoolTypeModernCards = ({ data }) => {
  const valueByType = Object.fromEntries(data.map((item) => [item.name, item.value]));
  return (
    <DashboardBarChart
      horizontal
      items={SCHOOL_TYPES.map((name) => ({ name, count: valueByType[name] || 0 }))}
      unit="schools"
    />
  );
};

const MediumStackedCards = ({ data }) => (
  <div className="w-full">
    <DashboardBarChart
      horizontal
      items={(data || []).map((item) => ({
        name: item.medium,
        count: item.total,
        percentage: item.percentage,
      }))}
      unit="schools"
    />
  </div>
);

const OverallSchoolLevelsCompact = ({
  primary,
  middle,
  secondary,
  higherSecondary,
}) => {
  // Always show all school levels, even if value is 0
  const items = [
    { name: "Primary", count: primary || 0 },
    { name: "Middle", count: middle || 0 },
    { name: "Secondary", count: secondary || 0 },
    { name: "Higher Secondary", count: higherSecondary || 0 },
  ];
  const total = items.reduce((sum, item) => sum + item.count, 0);
  return (
    <div>
      <DashboardBarChart horizontal items={items} unit="schools" />
      {total > 0 && (
        <div className="flex justify-between items-center pt-2 mx-4 border-t-2 border-gray-200">
          <span className="text-sm font-bold text-gray-700">Total Schools</span>
          <span className="text-xl font-bold text-[#137E81]">{total}</span>
        </div>
      )}
    </div>
  );
};
const CustomVerticalBarChart = ({ data }) => (
  <DashboardBarChart
    horizontal
    categories={(data || []).map((item) => item.district_name || "Unknown")}
    series={[
      { name: "Teaching", data: (data || []).map((item) => item.total_teaching_staff) },
      { name: "Non-Teaching", data: (data || []).map((item) => item.total_non_teaching_staff) },
    ]}
    unit="staff"
  />
);
const SchoolConditionPie = ({ schoolConditionData }) => {
  const chartData = useMemo(() => {
    const counts = {};
    schoolConditionData.forEach((item) => {
      counts[item.condition] = (counts[item.condition] || 0) + 1;
    });
    return Object.entries(counts).map(([condition, count], idx) => ({
      id: idx,
      value: count,
      label: condition,
      color: getPaletteColor(idx),
    }));
  }, [schoolConditionData]);
  if (!chartData.length) return <NoData />;
  return (
    <PieChart
      series={[
        {
          data: chartData,
        },
      ]}
      width={180}
      height={180}
    />
  );
};
const SchoolDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState("sme");
  const [smeData, setSmeData] = useState(null);
  const [microData, setMicroData] = useState(null);
  const [districtSummaryData, setDistrictSummaryData] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [schoolTypeData, setSchoolTypeData] = useState([]);
  const [ownershipData, setOwnershipData] = useState(null);
  const [libraryComputerData, setLibraryComputerData] = useState(null);
  const [districtStaffData, setDistrictStaffData] = useState([]);
  const [schoolConditionData, setSchoolConditionData] = useState([]);
  const [mediumData, setMediumData] = useState([]);
  const [schoolLevelData, setSchoolLevelData] = useState([]);
  const [schoolCounts, setSchoolCounts] = useState({
    surveyed: 0,
    approved: 0,
    rejected: 0,
  });
  const [districtSchoolLevelData, setDistrictSchoolLevelData] = useState([]);
  const [filters, setFilters] = useState({
    district_id: "",
    tehsil_id: "",
    uc_id: "",
    deh_id: "",
    village_id: "",
    dateField: "",
    condition: "",
    dateValue: "",
    startDate: "",
    endDate: "",
  });
  const [loading, setLoading] = useState(true);
  const formattedOwnershipData = ownershipData
    ? [
        {
          name: "Govt",
          value: Number(ownershipData.govt) || 0,
        },
        {
          name: "Rented",
          value: Number(ownershipData.rented) || 0,
        },
        {
          name: "Community",
          value: Number(ownershipData.community) || 0,
        },
        {
          name: "Donated",
          value: Number(ownershipData.donated) || 0,
        },
      ].filter((item) => item.value > 0)
    : [];
const totalPrimary = schoolLevelData
  .filter((item) => item?.school_level === "Primary")
  .reduce((sum, item) => sum + Number(item?.total_schools || 0), 0);

const totalMiddle = schoolLevelData
  .filter((item) => item?.school_level === "Middle")
  .reduce((sum, item) => sum + Number(item?.total_schools || 0), 0);

const totalSecondary = schoolLevelData
  .filter((item) => item?.school_level === "Secondary")
  .reduce((sum, item) => sum + Number(item?.total_schools || 0), 0);

const totalHigherSecondary = schoolLevelData
  .filter((item) => item?.school_level === "Higher Secondary")
  .reduce((sum, item) => sum + Number(item?.total_schools || 0), 0);
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toLocaleString();
    }
  };
  const formattedSchoolTypeData = schoolTypeData.map((item) => ({
    name: item.school_type,
    value: Number(item.total_schools) || 0,
  }));
  const formattedMediumData = useMemo(() => {
    return mediumData.map((item) => ({
      medium: item.medium,
      total: Number(item.total) || 0,
      percentage: item.percentage || "0.00",
    }));
  }, [mediumData]);
  useEffect(() => {
    fetchDistricts();
  }, []);
  useEffect(() => {
    fetchOwnershipData();
  }, []);
  useEffect(() => {
    fetchLibraryComputerData();
  }, []);
  useEffect(() => {
    fetchSchoolCounts();
  }, []);
  useEffect(() => {
    fetchDistrictSchoolLevelData();
  }, []);
  useEffect(() => {
    fetchDistrictStaffData();
  }, []);
  useEffect(() => {
    fetchSchoolLevelData();
  }, []);
  useEffect(() => {
    fetchSchoolTypeData();
  }, []);
  useEffect(() => {
    fetchMediumData();
  }, []);
  useEffect(() => {
    fetchSchoolConditionData();
  }, []);
  useEffect(() => {
    fetchGrantData();
  }, [activeSubTab]);
  const fetchSchoolCounts = async () => {
    try {
      const params = { ...filters };
      const response = await fetchData(
        "school-approved-reject-count",
        "GET",
        null,
        {},
        params,
        navigate,
        "/school-dashboard",
        dispatch
      );
      setSchoolCounts(
        response?.data || { surveyed: 0, approved: 0, rejected: 0 }
      );
    } catch (error) {
      console.error("Error fetching school counts:", error);
      setSchoolCounts({ surveyed: 0, approved: 0, rejected: 0 });
    }
  };
  const fetchDistrictSchoolLevelData = async () => {
    try {
      const params = { ...filters };
      const response = await fetchData(
        "district-wise-school-level",
        "GET",
        null,
        {},
        params,
        navigate,
        "/school-dashboard",
        dispatch
      );
      setDistrictSchoolLevelData(response?.data || []);
    } catch (error) {
      console.error("Error fetching district school level data:", error);
      setDistrictSchoolLevelData([]);
    }
  };
  const fetchOwnershipData = async () => {
    try {
      const params = {
        limit: "1000",
      };
      if (filters) {
        if (filters.district_id) params.district_id = filters.district_id;
        if (filters.tehsil_id) params.tehsil_id = filters.tehsil_id;
        if (filters.uc_id) params.uc_id = filters.uc_id;
        if (filters.deh_id) params.deh_id = filters.deh_id;
        if (filters.village_id) params.village_id = filters.village_id;
        if (filters.dateField) params.dateField = filters.dateField;
        if (filters.condition) params.condition = filters.condition;
        if (filters.dateValue) params.dateValue = filters.dateValue;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }
      const response = await fetchData(
        "get-building-ownership",
        "GET",
        null,
        {},
        filters,
        navigate,
        "/school-dashboard",
        dispatch
      );
      setOwnershipData(response?.data || null);
    } catch (error) {
      console.error("Error fetching building ownership data:", error);
      setOwnershipData(null);
    }
  };
  const fetchLibraryComputerData = async () => {
    try {
      const params = {
        limit: "1000",
      };
      if (filters) {
        if (filters.district_id) params.district_id = filters.district_id;
        if (filters.tehsil_id) params.tehsil_id = filters.tehsil_id;
        if (filters.uc_id) params.uc_id = filters.uc_id;
        if (filters.deh_id) params.deh_id = filters.deh_id;
        if (filters.village_id) params.village_id = filters.village_id;
        if (filters.dateField) params.dateField = filters.dateField;
        if (filters.condition) params.condition = filters.condition;
        if (filters.dateValue) params.dateValue = filters.dateValue;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }
      const response = await fetchData(
        "get-library-computer-stats",
        "GET",
        null,
        {},
        filters,
        navigate,
        "/school-dashboard",
        dispatch
      );
      setLibraryComputerData(response?.data || null);
    } catch (error) {
      console.error("Error fetching library/computer data:", error);
      setLibraryComputerData(null);
    }
  };
  const fetchDistrictStaffData = async () => {
    try {
      const params = {
        limit: "1000",
      };
      if (filters) {
        if (filters.district_id) params.district_id = filters.district_id;
        if (filters.tehsil_id) params.tehsil_id = filters.tehsil_id;
        if (filters.uc_id) params.uc_id = filters.uc_id;
        if (filters.deh_id) params.deh_id = filters.deh_id;
        if (filters.village_id) params.village_id = filters.village_id;
        if (filters.dateField) params.dateField = filters.dateField;
        if (filters.condition) params.condition = filters.condition;
        if (filters.dateValue) params.dateValue = filters.dateValue;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }
      const response = await fetchData(
        "district-wise-staff",
        "GET",
        null,
        {},
        filters,
        navigate,
        "/school-dashboard",
        dispatch
      );
      if (response?.success) {
        setDistrictStaffData(response.data || []);
      } else {
        setDistrictStaffData([]);
      }
    } catch (error) {
      console.error("Error fetching district staff data:", error);
      setDistrictStaffData([]);
    }
  };

  const fetchSchoolLevelData = async () => {
    try {
      const params = {
        limit: "1000",
      };
      if (filters) {
        if (filters.district_id) params.district_id = filters.district_id;
        if (filters.tehsil_id) params.tehsil_id = filters.tehsil_id;
        if (filters.uc_id) params.uc_id = filters.uc_id;
        if (filters.deh_id) params.deh_id = filters.deh_id;
        if (filters.village_id) params.village_id = filters.village_id;
        if (filters.dateField) params.dateField = filters.dateField;
        if (filters.condition) params.condition = filters.condition;
        if (filters.dateValue) params.dateValue = filters.dateValue;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }
      const response = await fetchData(
        "get-school-count-level",
        "GET",
        null,
        {},
        filters,
        navigate,
        "/school-dashboard",
        dispatch
      );
      setSchoolLevelData(response?.data || []);
    } catch (error) {
      console.error("Error fetching school level data:", error);
      setSchoolLevelData([]);
    }
  };
  const fetchSchoolTypeData = async () => {
    try {
      const response = await fetchData(
        "get-school-count-type",
        "GET",
        null,
        {},
        filters,
        navigate,
        "/school-dashboard",
        dispatch
      );
      setSchoolTypeData(response?.data || []);
    } catch (error) {
      console.error("Error fetching school type data:", error);
      setSchoolTypeData([]);
    }
  };
  const fetchGrantData = async () => {
    try {
      const endpoint = "get-district-wise-enrollment";
      const redirectPath = "/school-dashboard";
      const params = {
        limit: "1000",
      };
      if (filters) {
        if (filters.district_id) params.district_id = filters.district_id;
        if (filters.tehsil_id) params.tehsil_id = filters.tehsil_id;
        if (filters.uc_id) params.uc_id = filters.uc_id;
        if (filters.deh_id) params.deh_id = filters.deh_id;
        if (filters.village_id) params.village_id = filters.village_id;
        if (filters.dateField) params.dateField = filters.dateField;
        if (filters.condition) params.condition = filters.condition;
        if (filters.dateValue) params.dateValue = filters.dateValue;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }

      const response = await fetchData(
        endpoint,
        "GET",
        null,
        {},
        params,
        navigate,
        redirectPath,
        dispatch
      );
      const enrollmentData = Array.isArray(response?.data) ? response.data : [];
      setDistrictSummaryData(enrollmentData);
    } catch (error) {
      console.error("Error fetching enrollment data:", error);
      setDistrictSummaryData([]);
    }
  };
  
  const fetchDistricts = async () => {
    try {
      const params = {
        limit: "1000",
      };
      if (filters) {
        if (filters.district_id) params.district_id = filters.district_id;
        if (filters.tehsil_id) params.tehsil_id = filters.tehsil_id;
        if (filters.uc_id) params.uc_id = filters.uc_id;
        if (filters.deh_id) params.deh_id = filters.deh_id;
        if (filters.village_id) params.village_id = filters.village_id;
        if (filters.dateField) params.dateField = filters.dateField;
        if (filters.condition) params.condition = filters.condition;
        if (filters.dateValue) params.dateValue = filters.dateValue;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }
      const response = await fetchData(
        "districts",
        "GET",
        null,
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
      setDistricts([]);
    }
  };
  const fetchMediumData = async () => {
    try {
      const params = {
        limit: "1000",
      };
      if (filters) {
        if (filters.district_id) params.district_id = filters.district_id;
        if (filters.tehsil_id) params.tehsil_id = filters.tehsil_id;
        if (filters.uc_id) params.uc_id = filters.uc_id;
        if (filters.deh_id) params.deh_id = filters.deh_id;
        if (filters.village_id) params.village_id = filters.village_id;
        if (filters.dateField) params.dateField = filters.dateField;
        if (filters.condition) params.condition = filters.condition;
        if (filters.dateValue) params.dateValue = filters.dateValue;
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
      }
      const response = await fetchData(
        "School-Medium-Stats",
        "GET",
        null,
        {},
        params,
        navigate,
        "/school-dashboard",
        dispatch
      );
      setMediumData(response?.data || []);
    } catch (error) {
      console.error("Error fetching medium data:", error);
      setMediumData([]);
    }
  };
  const fetchSchoolConditionData = async () => {
    try {
      const response = await fetchData(
        "get-school-conditions",
        "GET",
        null,
        {},
        filters,
        navigate,
        "/school-dashboard",
        dispatch
      );
      if (response?.success) {
        setSchoolConditionData(response.data || []);
      } else {
        setSchoolConditionData([]);
      }
    } catch (error) {
      console.error("Error fetching district staff data:", error);
      setSchoolConditionData([]);
    }
  };
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const applyFilters = (appliedFilters) => {

    setFilters(appliedFilters);
  };
  useEffect(() => {
    if (filters) {
      setLoading(true);
      Promise.all([
        fetchOwnershipData(),
        fetchLibraryComputerData(),
        fetchDistrictStaffData(),
        fetchSchoolLevelData(),
        fetchSchoolTypeData(),
        fetchMediumData(),
        fetchSchoolConditionData(),
        fetchGrantData(),
        fetchSchoolCounts(),
        fetchDistrictSchoolLevelData(),
      ]).finally(() => setLoading(false));
    }
  }, [filters]);
  const has = {
    surveyed: hasData(schoolCounts.surveyed),
    resources: hasData(libraryComputerData?.total_library_books, libraryComputerData?.total_computers),
    ownership: hasData(formattedOwnershipData.map((item) => item.value)),
    staff: hasData(districtStaffData.map((d) => [d.total_teaching_staff, d.total_non_teaching_staff])),
    levelsByDistrict: hasData(
      districtSchoolLevelData.map((d) => [d.primary, d.middle, d.secondary, d.higher_secondary])
    ),
    medium: hasData(formattedMediumData.map((d) => d.total)),
    types: hasData(formattedSchoolTypeData.map((d) => d.value)),
    levels: hasData(totalPrimary, totalMiddle, totalSecondary, totalHigherSecondary),
    condition: schoolConditionData.length > 0,
    enrollment: districtSummaryData.length > 0,
  };

  const anyData = Object.values(has).some(Boolean);
  const showRow = () => loading || anyData;
  const panel = (hasPanelData, body) =>
    !loading && !hasPanelData ? <NoData /> : body;

  const resetFilters = () => {
    const initialFilters = {
      district_id: "",
      tehsil_id: "",
      uc_id: "",
      deh_id: "",
      village_id: "",
      dateField: "",
      condition: "",
      dateValue: "",
      startDate: "",
      endDate: "",
    };
    setFilters(initialFilters);
    fetchOwnershipData(initialFilters);
    fetchLibraryComputerData(initialFilters);
    fetchDistrictStaffData(initialFilters);
    fetchSchoolLevelData(initialFilters);
    fetchSchoolTypeData(initialFilters);
    fetchMediumData(initialFilters);
    fetchSchoolConditionData(initialFilters);
    fetchGrantData(initialFilters);
    fetchSchoolCounts();
    fetchDistrictSchoolLevelData();
  };
  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };
  return (
    <div className="w-full px-4 bg-gray-50">
      {/* Filters */}
      <div className="w-full flex flex-col justify-between space-y-3">
        <DashboardSearchFilters
          accordionTitle="Filters"
          defaultExpanded={false}
          fetchOnChange={false}
          apiRoutes={{
            districts: "districts",
            tehsils: "tehsils",
            ucs: "ucs",
            dehs: "dehs",
            villages: "villages",
          }}
          dateFields={["date_of_data_collection"]}
          onApply={(appliedFilters) => applyFilters(appliedFilters)}
        />
      </div>
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
          <p className="mt-2 text-gray-600">Loading school data...</p>
        </div>
      )}
      {/* Main Content */}
      <div className="bg-white border-dashed border-2 border-teal-700 min-h-40 w-full px-5 rounded-lg shadow-sm">
        <div>
          {!loading && !Object.values(has).some(Boolean) && (
            <NoData className="py-16" />
          )}
          {/* Row 1 - Three sections */}
          {showRow(has.surveyed, has.resources, has.ownership) && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* School surveyed Cards */}
            <div className="lg:col-span-4 text-center">
              <h3 className="text-xl my-2 text-[#3A3A3A] font-bold uppercase text-balance">
                School surveyed ({formatCount(schoolCounts.surveyed)})
              </h3>
              {panel(has.surveyed,
              <SchoolsurveyedCards
                surveyed={schoolCounts.surveyed}
                approved={schoolCounts.approved}
                rejected={schoolCounts.rejected}
              />)}
            </div>
            {/* Total Allocated - Donut */}
            <div className="lg:col-span-4 text-center">
              <h3 className="text-xl my-2 font-bold uppercase text-[#3A3A3A] mb-3 text-balance">
                Total Allocated
              </h3>
              {panel(has.resources,
              <AllocatedResourcesDonut
                libraryBooks={libraryComputerData?.total_library_books || 0}
                computers={libraryComputerData?.total_computers || 0}
              />)}
            </div>
            {/* Building Ownership Donut */}
            <div className="lg:col-span-4 text-center">
              <h3 className="text-xl my-2 font-bold uppercase text-[#3A3A3A] mb-3 text-balance">
                Building Ownership
              </h3>
              {panel(has.ownership,
              <DonutChart
                data={formattedOwnershipData}
                centerValue={formattedOwnershipData.reduce(
                  (sum, item) => sum + item.value,
                  0
                )}
                centerLabel="Total"
              />)}
            </div>
          </div>
          )}
          {/* Row 2 - Three sections: Teaching vs Non-Teaching Staff, School Levels by District, Applications by Medium */}
          {showRow(has.staff, has.levelsByDistrict, has.medium) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-4 first:hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-5 mb-2">
            {/* Teaching vs Non-Teaching Staff */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-lg flex flex-col">
                <h3 className="text-lg font-bold uppercase text-[#3A3A3A] text-center text-balance">
                  Teaching vs Non-Teaching Staff
                </h3>
                <div className="flex-1">
                  <CustomVerticalBarChart data={districtStaffData} />
                </div>
              </div>
            </div>
            {/* School Levels by District */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-lg flex flex-col">
                <h3 className="text-lg font-bold uppercase text-[#3A3A3A] text-center text-balance">
                  School Levels by District
                </h3>
                <div className="flex-1">
                  <SchoolLevelDistrictChart data={districtSchoolLevelData} />
                </div>
              </div>
            </div>
            {/* Applications by Medium */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-lg flex flex-col">
                <h3 className="text-lg font-bold uppercase text-[#3A3A3A] text-center text-balance">
                  School by Medium
                </h3>
                <div className="flex-1 flex items-center">
                  <MediumStackedCards data={formattedMediumData} />
                </div>
              </div>
            </div>
          </div>
          </>)}
          {/* Row 3 - Three sections: Applications by School Type, Overall School Levels, School Condition */}
          {showRow(has.types, has.levels, has.condition) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 my-4">
            {/* Applications by School Type */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-lg flex flex-col">
                <h3 className="text-xl text-[#3A3A3A] font-bold uppercase text-center text-balance">
                  School Type
                </h3>
                <div className="flex-1">
                  <SchoolTypeModernCards data={formattedSchoolTypeData} />
                </div>
              </div>
            </div>
            {/* Overall School Levels */}
            <div className="lg:col-span-4 text-center">
              <h3 className="text-xl font-bold uppercase text-[#3A3A3A] mb-3 text-balance">
                Overall School Levels
              </h3>
              {panel(has.levels,
              <OverallSchoolLevelsCompact
                primary={totalPrimary}
                middle={totalMiddle}
                secondary={totalSecondary}
                higherSecondary={totalHigherSecondary}
              />)}
            </div>
            {/* School Condition */}
            <div className="lg:col-span-4 bg-white rounded-lg flex flex-col">
              <h3 className="text-lg font-bold uppercase text-[#3A3A3A] text-center text-balance">
                School Condition
              </h3>
              <div className="flex-1 flex justify-center items-center">
                <SchoolConditionPie schoolConditionData={schoolConditionData} />
              </div>
            </div>
          </div>
          </>)}
          {/* Row 4 - Full Width Summary Table */}
          {showRow(has.enrollment) && (<>
          <hr className="border-t-2 border-dashed border-teal-700 my-2 first:hidden" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-12">
              <h3 className="text-xl font-bold uppercase text-[#3A3A3A] my-2 text-center text-balance">
                Summary of Enrollment Data
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-white bg-[#137e81] font-medium">
                        District
                      </th>
                      <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                        Total Boys
                      </th>
                      <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                        Total Girls
                      </th>
                      <th className="px-4 py-3 text-center text-white bg-[#137e81] font-medium">
                        Total Enrollment
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {districtSummaryData?.length > 0 ? (
                      districtSummaryData.map((item, index) => (
                        <tr
                          key={item.district_id}
                          className={`${
                            index % 2 === 0 ? "bg-[#F6F7F9]" : "bg-white"
                          } hover:bg-gray-100`}
                        >
                          <td className="px-4 py-3 text-gray-800 border-b font-medium">
                            {item.district?.district_name || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-center border-b">
                            {formatCount(parseInt(item.total_boys) || 0)}
                          </td>
                          <td className="px-4 py-3 text-center border-b">
                            {formatCount(parseInt(item.total_girls) || 0)}
                          </td>
                          <td className="px-4 py-3 text-center border-b font-semibold">
                            {formatCount(parseInt(item.total_enrollment) || 0)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          {loading ? "Loading data..." : "No data available"}
                        </td>
                      </tr>
                    )}
                    <tr className="bg-[#137e81] text-white font-bold">
                      <td className="px-4 py-3 border-b text-white">Total</td>
                      <td className="px-4 py-3 text-center border-b">
                        {formatCount(
                          districtSummaryData.reduce(
                            (sum, i) => sum + (parseInt(i.total_boys) || 0),
                            0
                          )
                        )}
                      </td>
                      <td className="px-4 py-3 text-center border-b">
                        {formatCount(
                          districtSummaryData.reduce(
                            (sum, i) => sum + (parseInt(i.total_girls) || 0),
                            0
                          )
                        )}
                      </td>
                      <td className="px-4 py-3 text-center border-b">
                        {formatCount(
                          districtSummaryData.reduce(
                            (sum, i) =>
                              sum + (parseInt(i.total_enrollment) || 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
};
export default SchoolDashboard;
